import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Plus, Pencil, Trash2, HelpCircle, TrendingUp, Search } from 'lucide-react';

interface AgentFAQ {
  id: string;
  company_config_id: string;
  category: string;
  question: string;
  answer: string;
  keywords: string[];
  usage_count: number;
  effectiveness_score: number;
  is_active: boolean;
  priority: number;
  created_at: string;
  updated_at: string;
  company_config?: {
    name?: string;
  };
}

interface Agent {
  id: string;
  name: string;
}

const FAQ_CATEGORIES = [
  'pricing',
  'services',
  'scheduling',
  'emergency',
  'payment',
  'warranty',
  'membership',
  'coverage_area',
  'technicians',
  'policies',
  'equipment',
  'other'
];

const AgentFAQsPage: React.FC = () => {
  const [faqs, setFaqs] = useState<AgentFAQ[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState<AgentFAQ | null>(null);
  
  // Filters
  const [selectedAgent, setSelectedAgent] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    company_config_id: '',
    category: 'pricing',
    question: '',
    answer: '',
    keywords: [] as string[],
    priority: 5,
    is_active: true
  });

  useEffect(() => {
    fetchAgents();
    fetchFAQs();
  }, []);

  const fetchAgents = async () => {
    const { data, error } = await supabase
      .from('company_configs')
      .select('id, company_name')
      .eq('is_active', true)
      .order('company_name');

    if (error) {
      console.error('Error fetching agents:', error);
    } else {
      setAgents(data?.map((a: any) => ({ id: a.id, name: a.company_name })) || []);
    }
  };

  const fetchFAQs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('agent_faqs')
      .select(`
        *,
        company_config:company_configs!agent_faqs_company_config_id_fkey(name:company_name)
      `)
      .order('priority', { ascending: false })
      .order('usage_count', { ascending: false });

    if (error) {
      console.error('Error fetching FAQs:', error);
    } else {
      setFaqs(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const faqData = {
      ...formData,
      updated_at: new Date().toISOString()
    };

    if (editingFAQ) {
      const { error } = await supabase
        .from('agent_faqs')
        .update(faqData)
        .eq('id', editingFAQ.id);

      if (error) {
        console.error('Error updating FAQ:', error);
        alert('Failed to update FAQ');
      } else {
        await fetchFAQs();
        closeModal();
      }
    } else {
      const { error } = await supabase
        .from('agent_faqs')
        .insert([{ 
          ...faqData, 
          created_at: new Date().toISOString(),
          usage_count: 0,
          effectiveness_score: 0
        }]);

      if (error) {
        console.error('Error creating FAQ:', error);
        alert('Failed to create FAQ');
      } else {
        await fetchFAQs();
        closeModal();
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    const { error } = await supabase
      .from('agent_faqs')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting FAQ:', error);
      alert('Failed to delete FAQ');
    } else {
      await fetchFAQs();
    }
  };

  const openModal = (faq?: AgentFAQ) => {
    if (faq) {
      setEditingFAQ(faq);
      setFormData({
        company_config_id: faq.company_config_id,
        category: faq.category,
        question: faq.question,
        answer: faq.answer,
        keywords: faq.keywords || [],
        priority: faq.priority,
        is_active: faq.is_active
      });
    } else {
      setEditingFAQ(null);
      setFormData({
        company_config_id: agents[0]?.id || '',
        category: 'pricing',
        question: '',
        answer: '',
        keywords: [],
        priority: 5,
        is_active: true
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingFAQ(null);
  };

  const getCategoryLabel = (category: string) => {
    return category.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      pricing: 'bg-purple-100 text-purple-800',
      services: 'bg-blue-100 text-blue-800',
      scheduling: 'bg-green-100 text-green-800',
      emergency: 'bg-red-100 text-red-800',
      payment: 'bg-yellow-100 text-yellow-800',
      warranty: 'bg-indigo-100 text-indigo-800',
      membership: 'bg-pink-100 text-pink-800',
      coverage_area: 'bg-teal-100 text-teal-800',
      technicians: 'bg-orange-100 text-orange-800',
      policies: 'bg-cyan-100 text-cyan-800',
      equipment: 'bg-gray-100 text-gray-800',
      other: 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors.other;
  };

  const getEffectivenessColor = (score: number) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredFAQs = faqs.filter(faq => {
    if (selectedAgent !== 'all' && faq.company_config_id !== selectedAgent) return false;
    if (selectedCategory !== 'all' && faq.category !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query) ||
        faq.keywords.some(k => k.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent FAQs Manager</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage frequently asked questions with usage tracking and effectiveness scoring
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Add FAQ
        </button>
      </div>

      <div className="mb-6 p-4 rounded-lg border border-blue-200 bg-blue-50 text-blue-900">
        <p className="text-sm">
          About this page: Manage per-company FAQs backed by the <span className="font-mono">agent_faqs</span> table (legacy).
          Prefer storing FAQs in the unified <span className="font-mono">knowledge_articles</span> table with <span className="font-mono">article_type="faq"</span>,
          visible on the <a href="/ai-voice-agents/knowledge-base" className="underline font-medium">Knowledge Base</a> page.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Agent
            </label>
            <select
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Agents</option>
              {agents.map(agent => (
                <option key={agent.id} value={agent.id}>{agent.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filter by Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="all">All Categories</option>
              {FAQ_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search FAQs..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
          </div>
        </div>
      </div>

      {/* FAQs Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading FAQs...</p>
        </div>
      ) : filteredFAQs.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-600">No FAQs found. Create your first FAQ to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFAQs.map(faq => (
            <div
              key={faq.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-4"
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <HelpCircle size={16} className="text-blue-600" />
                    <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(faq.category)}`}>
                      {getCategoryLabel(faq.category)}
                    </span>
                    {!faq.is_active && (
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                        Inactive
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-1">
                    Q: {faq.question}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    {faq.company_config?.name || 'Unknown Agent'}
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openModal(faq)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                A: {faq.answer}
              </p>

              {faq.keywords && faq.keywords.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {faq.keywords.slice(0, 3).map((keyword, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                      {keyword}
                    </span>
                  ))}
                  {faq.keywords.length > 3 && (
                    <span className="text-xs text-gray-500">
                      +{faq.keywords.length - 3}
                    </span>
                  )}
                </div>
              )}

              <div className="flex justify-between items-center text-xs pt-3 border-t">
                <div className="flex items-center gap-3">
                  <span className="text-gray-600">
                    Used: {faq.usage_count}x
                  </span>
                  {faq.effectiveness_score > 0 && (
                    <div className="flex items-center gap-1">
                      <TrendingUp size={12} className={getEffectivenessColor(faq.effectiveness_score)} />
                      <span className={getEffectivenessColor(faq.effectiveness_score)}>
                        {(faq.effectiveness_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                </div>
                <span className="text-gray-500">
                  P{faq.priority}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">
                {editingFAQ ? 'Edit FAQ' : 'Add New FAQ'}
              </h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Agent *
                    </label>
                    <select
                      required
                      value={formData.company_config_id}
                      onChange={(e) => setFormData({ ...formData, company_config_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select Agent</option>
                      {agents.map(agent => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category *
                    </label>
                    <select
                      required
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      {FAQ_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{getCategoryLabel(cat)}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Question *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.question}
                      onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., What are your service hours?"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Answer *
                    </label>
                    <textarea
                      required
                      value={formData.answer}
                      onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      rows={5}
                      placeholder="Provide a clear, concise answer..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Keywords (comma-separated)
                    </label>
                    <input
                      type="text"
                      value={formData.keywords.join(', ')}
                      onChange={(e) => setFormData({ ...formData, keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="e.g., hours, schedule, availability"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Keywords help the AI agent find the right FAQ quickly
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority (1-10) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10"
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Higher priority FAQs are presented first (10 = highest)
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded border-gray-300"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                      FAQ is Active
                    </label>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    {editingFAQ ? 'Update FAQ' : 'Create FAQ'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentFAQsPage;
